import React from "react";

import { StickyFrame } from "~/components/table/sticky-frame";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { prop } from "~/tools/prop";
import { spacing } from "~/tools/spacing";
import { useDelayed } from "~/tools/use-delayed";

import type { OverviewPageResponse } from "../../../../common/openapi";
import { useUserProfile } from "../../../../common/use-user-profile";
import { OverviewTable } from "../../../../components/overview-table";
import { useOverviewTableFiltering } from "../../../../components/overview-table/use-filtering";
import { useOverviewTableSorting } from "../../../../components/overview-table/use-sorting";
import { ErrorScreen } from "../../../../components/react/full-center-view-layout/error-screen";
import { type QueryOverviewParams, queryOverview, toQueryOverviewResponse } from "../../../../queries/overview";
import { useInfiniteQuery } from "../../../../queries/react-query";

import type { RelatedResourcesDrawerState } from "./state";

const QUERY_PARAMETERS_DELAY_MS = 200;
const PAGE_SIZE = 30;

interface ResourceTableProps extends Pick<RelatedResourcesDrawerState, "timestamp" | "timeRange"> {
	stql: undefined | string;
	presentationIdentifier: undefined | string;
}

export const ResourceTable: React.FC<ResourceTableProps> = ({ timestamp, timeRange, stql, presentationIdentifier }) => {
	const { sorting, orders } = useOverviewTableSorting();
	const { filtering, filters } = useOverviewTableFiltering();

	const delayed = useDelayed(
		{ sorting, columnFilters: filtering } satisfies Partial<QueryOverviewParams>,
		QUERY_PARAMETERS_DELAY_MS,
		{
			compare: isDeepEqual,
		},
	);

	const [hideUnavailableMetrics = false] = useUserProfile({
		select: prop("hideUnavailableMetrics"),
	});

	const { data, error, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery<OverviewPageResponse, Error>(
		{
			enabled: presentationIdentifier != null && stql != null,

			refetchOnMount: false,
			refetchOnReconnect: false,
			refetchOnWindowFocus: false,
			refetchOnTimelineSummaryUpdate: true,

			...queryOverview({
				...delayed,
				timestamp,
				urn: presentationIdentifier ?? "",
				stql: stql ?? "",
				limit: PAGE_SIZE,
			}),
		},
	);

	const response = React.useMemo(
		() => toQueryOverviewResponse({ hideUnavailableMetrics, error, data }),
		[hideUnavailableMetrics, error, data],
	);

	if (response.isFailure()) {
		return <ErrorScreen error={response.error} noShadow />;
	}

	return (
		<StickyFrame className="bg-grey-100 pb-4" frames={[spacing(4)]}>
			<OverviewTable
				cols={response.transform(prop("columns")).getOrElse(undefined)}
				fetchNextPage={() => {
					if (hasNextPage && !isFetchingNextPage) {
						fetchNextPage();
					}
				}}
				filters={filters}
				fixedColumns={response.transform(prop("fixedColumns")).getOrElse(undefined)}
				orders={orders}
				remaining={response.transform(({ filtered, rows }) => filtered - rows.length).getOrElse(PAGE_SIZE)}
				rows={response.transform(prop("rows")).getOrElse(undefined)}
				timeRange={timeRange}
				timestamp={timestamp}
			/>
		</StickyFrame>
	);
};
